/**
 * Typed `SyncEvent` bus (phase4-sync-strangler.md §D3): the transport layer
 * announces WHAT happened; presentation decides what the user sees.
 *
 * Producers: the sync orchestrator and firebase-config — which must never
 * import `useToastStore` (lint-enforced for src/lib/sync/** and
 * src/domains/sync/**).
 *
 * Consumer: exactly ONE — `src/app/sync/wireSyncEvents.ts`, registered by
 * the `syncInit` boot task. It owns every user-facing sync string and the
 * `useSyncStore` mirror writes (lastSyncTime is driven by `flushed`).
 *
 * Beyond the bare fact, some events carry the context the toast copy is
 * keyed on:
 *  - `save-rejected`/`workspace-tombstoned` carry permissionDenied /
 *    connect-vs-switch;
 *  - `switch` distinguishes 'failed-aborted' (the NON-destructive pre-apply
 *    failure path) from 'failed-rolling-back';
 *  - `local-persistence-unavailable` is firebase-config's one toast.
 */
import type { FirestoreSyncStatus, FirebaseAuthStatus } from '~types/sync';
import { createLogger } from '@lib/logger';

const logger = createLogger('SyncEvents');

export type SyncEvent =
  | { type: 'status'; status: FirestoreSyncStatus }
  | { type: 'auth'; status: FirebaseAuthStatus; email: string | null }
  /** A committed save (both transports: the mock and y-cinder's `saved` event). */
  | { type: 'flushed'; at: number }
  | { type: 'clean-sync'; phase: 'started' | 'applied' | 'failed' }
  | {
      type: 'switch';
      phase:
        | 'downloading'
        | 'verifying'
        | 'staged'
        | 'applying'
        | 'failed-rolling-back'
        | 'failed-aborted';
    }
  | {
      type: 'save-rejected';
      code: 'permission-denied' | 'document-too-large' | 'max-retries-exceeded';
      sizeBytes?: number;
      permissionDenied: boolean;
    }
  | { type: 'connection-error'; permissionDenied: boolean }
  | { type: 'sync-failure'; permissionDenied: boolean }
  | { type: 'workspace-tombstoned'; workspaceId: string; context: 'connect' | 'switch' }
  | { type: 'workspace-purged'; report: { docsDeleted: number; blobsDeleted: number } }
  /** Doc-level quarantine fired (§D5): the client is obsolete vs the fleet. */
  | { type: 'obsolete'; incomingVersion: number }
  /**
   * The replicated document was squashed into a new epoch (y-cinder's
   * long-lived-document floor reset — see y-cinder docs/performance.md).
   * The provider has stopped syncing: old-epoch and new-epoch id spaces
   * cannot merge. `self` is true on the device that ran the squash.
   * Recovery = rebuild the local doc from the new epoch's snapshot (the
   * staged-swap machinery is the intended vehicle) and reconnect.
   */
  | { type: 'epoch-changed'; epoch: number; previousEpoch: number; self: boolean }
  | { type: 'local-persistence-unavailable' };

export interface SyncEventBus {
  emit(event: SyncEvent): void;
  /** Subscribe; returns the unsubscribe handle. */
  on(listener: (event: SyncEvent) => void): () => void;
}

function createSyncEventBus(): SyncEventBus {
  const listeners = new Set<(event: SyncEvent) => void>();
  return {
    emit: (event) => {
      for (const listener of [...listeners]) {
        try {
          listener(event);
        } catch (e) {
          // A presentation bug must never take down the transport.
          logger.error('Sync event listener threw:', e);
        }
      }
    },
    on: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

let bus: SyncEventBus | null = null;

/** The process-wide bus (transport singleton, like the sync manager). */
export function getSyncEventBus(): SyncEventBus {
  if (!bus) bus = createSyncEventBus();
  return bus;
}
